import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, forkJoin } from 'rxjs';
import {
  BreadcrumbItem,
  BreadcrumbsComponent,
} from '../../../../shared/components/breadcrumbs/breadcrumbs.component';
import {
  CASE_REFERRAL_SOURCE_OPTIONS,
  CaseApiModel,
  CaseStatus,
  CASE_NOTE_CONTACT_TYPE_OPTIONS,
  CaseNoteContactType,
  CASE_NOTE_LOCATION_TYPE_OPTIONS,
  CaseNoteLocationType,
  CaseReferralSource,
  CaseNoteApiModel,
  CaseStatusEventApiModel,
  CasesService,
  CreateCaseNoteRequest,
  PartnerOption,
  ServiceOption,
} from '../../../cases/services/cases.service';
import { ClientDetailApiModel } from '../../models/client.models';
import { ClientsService } from '../../services/clients.service';
import { DateFormatService } from '../../../../core/services/date-format.service';
import { ToastService } from '../../../../core/services/toast.service';

@Component({
  selector: 'app-client-case',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, BreadcrumbsComponent],
  templateUrl: './client-case.component.html',
  styleUrl: './client-case.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientCaseComponent {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly clientsService = inject(ClientsService);
  private readonly casesService = inject(CasesService);
  private readonly dateFormatService = inject(DateFormatService);
  private readonly toastService = inject(ToastService);

  readonly clientId = signal<number | null>(null);
  readonly caseId = signal<number | null>(null);

  readonly loadingClient = signal(false);
  readonly loadingLookups = signal(false);
  readonly loadingCase = signal(false);
  readonly loadingNotes = signal(false);
  readonly savingCase = signal(false);
  readonly togglingStatus = signal(false);
  readonly savingNote = signal(false);
  readonly noteSubmitAttempted = signal(false);
  readonly pendingStatusChange = signal<CaseStatus | null>(null);
  readonly pendingStatusSummary = signal('');

  readonly client = signal<ClientDetailApiModel | null>(null);
  readonly caseItem = signal<CaseApiModel | null>(null);
  readonly services = signal<ServiceOption[]>([]);
  readonly partners = signal<PartnerOption[]>([]);
  readonly notes = signal<CaseNoteApiModel[]>([]);
  readonly statusEvents = signal<CaseStatusEventApiModel[]>([]);

  readonly caseForm = this.fb.nonNullable.group({
    serviceId: [0, [Validators.required, Validators.min(1)]],
    partnerOrganizationId: [0, [Validators.required, Validators.min(1)]],
    referralSource: ['' as CaseReferralSource | '', [Validators.required]],
    referralSourceOther: ['', [Validators.maxLength(255)]],
    referralDate: ['', [Validators.required]],
    priority: ['medium' as 'low' | 'medium' | 'high', [Validators.required]],
  });

  readonly referralSourceOptions: Array<{ value: CaseReferralSource; label: string }> = [
    { value: 'hospital', label: 'Hospital' },
    { value: 'school', label: 'School' },
    { value: 'community_based_organization', label: 'Community Based Organization' },
    { value: 'self', label: 'Self' },
    { value: 'other', label: 'Other' },
  ];

  readonly caseStatusOptions: Array<{ value: CaseStatus; label: string }> = [
    { value: 'open', label: 'Open' },
    { value: 'closed', label: 'Closed' },
    { value: 'declined_closed', label: 'Declined/Closed' },
  ];

  readonly noteContactTypeOptions: Array<{ value: CaseNoteContactType; label: string }> = [
    { value: 'street_outreach', label: 'Street Outreach' },
    { value: 'home_visit', label: 'Home Visit' },
    { value: 'phone', label: 'Phone' },
    { value: 'text', label: 'Text' },
  ];

  readonly noteLocationTypeOptions: Array<{ value: CaseNoteLocationType; label: string }> = [
    { value: 'community', label: 'Community' },
    { value: 'school', label: 'School' },
    { value: 'hospital', label: 'Hospital' },
    { value: 'virtual', label: 'Virtual' },
  ];

  readonly noteForm = this.fb.nonNullable.group({
    note: ['', [Validators.required, Validators.maxLength(5000)]],
    noteDate: [this.todayDateString(), [Validators.required]],
    startTime: [''],
    endTime: [''],
    contactType: ['street_outreach' as CaseNoteContactType, [Validators.required]],
    locationType: ['community' as CaseNoteLocationType, [Validators.required]],
    followUpRequired: [false],
    serviceUnits: [''],
  });

  readonly pageLoading = computed(
    () => this.loadingClient() || this.loadingLookups() || this.loadingCase(),
  );

  readonly timelineEvents = computed(() => {
    const events: Array<{ sortKey: number; type: 'created' | 'note' | 'status'; payload: unknown }> = [];
    const activeCase = this.caseItem();

    if (activeCase) {
      events.push({
        sortKey: new Date(activeCase.openedAt).getTime(),
        type: 'created',
        payload: activeCase,
      });
    }

    for (const note of this.notes()) {
      events.push({
        sortKey: new Date(note.createdAt).getTime(),
        type: 'note',
        payload: note,
      });
    }

    for (const statusEvent of this.statusEvents()) {
      events.push({
        sortKey: new Date(statusEvent.createdAt).getTime(),
        type: 'status',
        payload: statusEvent,
      });
    }

    return events.sort((a, b) => b.sortKey - a.sortKey);
  });

  readonly noteDurationHours = computed(() => {
    const startTime = this.noteForm.controls.startTime.getRawValue().trim();
    const endTime = this.noteForm.controls.endTime.getRawValue().trim();
    if (!startTime || !endTime) {
      return null;
    }

    const minutes = this.computeDurationMinutes(startTime, endTime);
    if (minutes < 0) {
      return null;
    }

    return Math.round((minutes / 60) * 100) / 100;
  });

  readonly mode = computed<'create' | 'detail'>(() =>
    this.caseId() && this.caseId()! > 0 ? 'detail' : 'create',
  );

  readonly clientDisplayName = computed(() => {
    const source = this.client();
    if (!source) {
      return 'Client';
    }

    const names = [source.firstName, source.middleName, source.lastName]
      .map((value) => (value ?? '').trim())
      .filter(Boolean);

    return names.join(' ') || 'Client';
  });

  readonly serviceName = computed(() => {
    const item = this.caseItem();
    if (!item?.serviceId) {
      return 'Unassigned service';
    }
    return this.services().find((service) => service.id === item.serviceId)?.name ?? 'Service';
  });

  readonly detailServiceUnitLabel = computed(() => {
    const activeCase = this.caseItem();
    if (!activeCase?.serviceId) {
      return 'Units';
    }

    return this.services().find((service) => service.id === activeCase.serviceId)?.unitLabel ?? 'Units';
  });

  readonly latestCaseNote = computed(() => {
    const allNotes = this.notes();
    return allNotes.length > 0 ? allNotes[allNotes.length - 1] : null;
  });

  readonly breadcrumbItems = computed<BreadcrumbItem[]>(() => {
    const clientId = this.clientId();
    const activeCase = this.caseItem();
    const modeLabel = this.mode() === 'create'
      ? 'Create Case'
      : (activeCase?.caseUniqueId ? `Case ${activeCase.caseUniqueId}` : 'Case');

    return [
      { label: 'Home', route: '/home' },
      ...(clientId ? [{ label: this.clientDisplayName(), route: `/clients/${clientId}` }] : []),
      { label: modeLabel },
    ];
  });

  readonly partnerName = computed(() => {
    const item = this.caseItem();
    if (!item?.assignedPartnerOrganizationId) {
      return 'Unassigned partner';
    }
    return this.partners().find((partner) => partner.id === item.assignedPartnerOrganizationId)?.name
      ?? 'Partner';
  });

  readonly caseStatusLabel = computed(() => this.formatCaseStatusLabel(this.caseItem()?.status ?? null));

  readonly referralSourceLabel = computed(() => {
    const source = this.caseItem()?.referralSource;
    if (!source) {
      return 'Unknown';
    }

    if (source === 'community_based_organization') {
      return 'Community Based Organization';
    }

    if (source === 'other') {
      const detail = this.caseItem()?.referralSourceOther?.trim();
      return detail ? `Other (${detail})` : 'Other';
    }

    return source.charAt(0).toUpperCase() + source.slice(1);
  });

  constructor() {
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      const parsedClientId = this.parsePositiveInt(params.get('id'));
      const parsedCaseId = this.parsePositiveInt(params.get('case_id'));
      this.initializeForRoute(parsedClientId, parsedCaseId);
    });

    this.caseForm.controls.serviceId.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => {
        if (this.mode() !== 'create') {
          return;
        }

        const serviceId = Number(value);
        if (!serviceId || serviceId <= 0) {
          this.partners.set([]);
          this.caseForm.controls.partnerOrganizationId.setValue(0);
          return;
        }

        this.loadPartnersByService(serviceId);
      });

    this.caseForm.controls.referralSource.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => {
        if (value !== 'other') {
          this.caseForm.controls.referralSourceOther.setValue('');
        }
      });

  }

  submitCreateCase(): void {
    if (this.mode() !== 'create') {
      return;
    }

    if (this.caseForm.invalid || this.savingCase()) {
      this.caseForm.markAllAsTouched();
      return;
    }

    const clientId = this.clientId();
    if (!clientId) {
      this.toastService.danger('Create Case Failed', 'Cannot create case: invalid client reference.');
      return;
    }

    const raw = this.caseForm.getRawValue();
    const referralSource = raw.referralSource as CaseReferralSource;

    if (!CASE_REFERRAL_SOURCE_OPTIONS.includes(referralSource)) {
      this.toastService.warning('Referral Source Required', 'Referral source is required.');
      return;
    }

    if (referralSource === 'other' && !raw.referralSourceOther.trim()) {
      this.toastService.warning(
        'Referral Source Detail Required',
        'Please provide referral source detail when selecting Other.',
      );
      return;
    }

    this.savingCase.set(true);

    this.casesService
      .createCase({
        clientId,
        serviceId: Number(raw.serviceId),
        assignedPartnerOrganizationId: Number(raw.partnerOrganizationId),
        referralSource,
        referralSourceOther: referralSource === 'other' ? raw.referralSourceOther.trim() : undefined,
        referralDate: raw.referralDate,
        priority: raw.priority,
        status: 'open',
      })
      .pipe(finalize(() => this.savingCase.set(false)))
      .subscribe({
        next: ({ data }) => {
          this.toastService.success('Case Created', 'Case created successfully.');
          void this.router.navigate(['/clients', clientId, 'case', data.id]);
        },
        error: (error) => {
          this.toastService.danger(
            'Create Case Failed',
            error?.error?.message ?? 'Unable to create case right now.',
          );
        },
      });
  }

  goBackToClient(): void {
    const clientId = this.clientId();
    if (!clientId) {
      return;
    }
    void this.router.navigate(['/clients', clientId]);
  }

  goBackHome(): void {
    void this.router.navigate(['/home']);
  }

  copyCaseUniqueId(): void {
    const uniqueId = this.caseItem()?.caseUniqueId?.trim();
    if (!uniqueId) {
      this.toastService.danger('Copy Failed', 'Case ID is not available.');
      return;
    }

    if (!('clipboard' in navigator) || typeof navigator.clipboard?.writeText !== 'function') {
      this.toastService.danger('Copy Failed', 'Clipboard is not supported in this browser.');
      return;
    }

    void navigator.clipboard
      .writeText(uniqueId)
      .then(() => {
        this.toastService.success('Case ID Copied', uniqueId);
      })
      .catch(() => {
        this.toastService.danger('Copy Failed', 'Unable to copy Case ID right now.');
      });
  }

  formatDateTime(value: string | null): string {
    return this.dateFormatService.formatDateTime(value);
  }

  formatNoteDate(value: string | null): string {
    return this.dateFormatService.formatDate(value);
  }

  submitNote(): void {
    const caseId = this.caseId();
    if (!caseId || this.savingNote()) {
      return;
    }

    this.noteSubmitAttempted.set(true);

    if (this.noteForm.invalid) {
      this.noteForm.markAllAsTouched();
      return;
    }

    const raw = this.noteForm.getRawValue();
    const note = raw.note.trim();
    const noteDate = raw.noteDate.trim();
    const startTime = raw.startTime.trim();
    const endTime = raw.endTime.trim();

    if (!note) {
      this.toastService.warning('Note Required', 'Case note text is required.');
      return;
    }

    if ((startTime && !endTime) || (!startTime && endTime)) {
      this.toastService.warning('Invalid Time Range', 'Start time and end time must both be provided.');
      return;
    }

    if (startTime && endTime) {
      const minutes = this.computeDurationMinutes(startTime, endTime);
      if (minutes < 0) {
        this.toastService.warning('Invalid Time Range', 'End time must be after start time.');
        return;
      }
    }

    let serviceUnitsNumber: number | undefined;
    const serviceUnitsText = String(raw.serviceUnits ?? '').trim();
    if (serviceUnitsText.length > 0) {
      const parsed = Number(serviceUnitsText);
      if (!Number.isFinite(parsed) || parsed < 0) {
        this.toastService.warning(
          'Invalid Service Units',
          'Service units must be a non-negative number.',
        );
        return;
      }
      serviceUnitsNumber = parsed;
    }

    const payload: CreateCaseNoteRequest = {
      note,
      noteDate,
      startTime: startTime || undefined,
      endTime: endTime || undefined,
      contactType: raw.contactType,
      locationType: raw.locationType,
      followUpRequired: raw.followUpRequired,
      serviceUnitOfMeasure: this.detailServiceUnitLabel(),
      serviceUnits: serviceUnitsNumber,
    };

    this.savingNote.set(true);

    this.casesService
      .createCaseNote(caseId, payload)
      .pipe(finalize(() => this.savingNote.set(false)))
      .subscribe({
        next: ({ data }) => {
          this.notes.set([...this.notes(), data]);
          this.noteForm.reset({
            note: '',
            noteDate: this.todayDateString(),
            startTime: '',
            endTime: '',
            contactType: 'street_outreach',
            locationType: 'community',
            followUpRequired: false,
            serviceUnits: '',
          });
          this.noteSubmitAttempted.set(false);
          this.toastService.success('Case Note Added', 'Case note added.');
          this.refreshCaseSummary(caseId);
        },
        error: (error) => {
          this.toastService.danger(
            'Add Note Failed',
            error?.error?.message ?? 'Unable to add note right now.',
          );
        },
      });
  }

  requestCaseStatusChange(nextStatus: CaseStatus): void {
    const item = this.caseItem();
    if (!item || this.togglingStatus() || item.status === nextStatus) {
      return;
    }

    if (nextStatus === 'closed' || nextStatus === 'declined_closed') {
      this.pendingStatusSummary.set(item.closureSummary?.trim() ?? '');
      this.pendingStatusChange.set(nextStatus);
      return;
    }

    this.applyCaseStatus(nextStatus, null);
  }

  cancelStatusChange(): void {
    this.pendingStatusChange.set(null);
    this.pendingStatusSummary.set('');
  }

  confirmStatusChange(): void {
    const nextStatus = this.pendingStatusChange();
    if (!nextStatus) {
      return;
    }

    const summary = this.pendingStatusSummary().trim();
    if ((nextStatus === 'closed' || nextStatus === 'declined_closed') && !summary) {
      this.toastService.warning('Summary Required', 'Please provide a summary before closing this case.');
      return;
    }

    this.applyCaseStatus(nextStatus, summary || null);
    this.cancelStatusChange();
  }

  private applyCaseStatus(nextStatus: CaseStatus, closureSummary: string | null): void {
    const item = this.caseItem();
    if (!item) {
      return;
    }

    this.togglingStatus.set(true);

    this.casesService
      .updateCaseStatus(item.id, {
        status: nextStatus,
        closureSummary: closureSummary ?? undefined,
      })
      .pipe(finalize(() => this.togglingStatus.set(false)))
      .subscribe({
        next: ({ data }) => {
          this.caseItem.set(data);
          this.toastService.success(
            data.status === 'open'
              ? 'Case Reopened'
              : (data.status === 'declined_closed' ? 'Case Declined/Closed' : 'Case Closed'),
            data.status === 'open'
              ? 'Case reopened successfully.'
              : 'Case status updated successfully.',
            5000,
          );
          this.loadStatusEvents(item.id, item.clientId);
        },
        error: (error) => {
          this.toastService.danger(
            'Update Status Failed',
            error?.error?.message ?? 'Unable to update case status right now.',
          );
        },
      });
  }

  formatCaseStatusLabel(status: CaseStatus | null): string {
    if (!status) {
      return 'Unknown';
    }

    if (status === 'declined_closed') {
      return 'Declined/Closed';
    }

    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  formatContactType(value: CaseNoteContactType | null): string {
    if (!value) {
      return 'N/A';
    }

    return value
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  formatLocationType(value: CaseNoteLocationType | null): string {
    if (!value) {
      return 'N/A';
    }

    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  getCaseCreatorLabel(caseItem: CaseApiModel): string {
    const createdByName = caseItem.createdByName?.trim();
    if (createdByName) {
      return createdByName;
    }

    return `User #${caseItem.createdByUserId}`;
  }

  getCaseAssignedPartnerLabel(caseItem: CaseApiModel): string {
    const partnerName = caseItem.partnerName?.trim();
    if (partnerName) {
      return partnerName;
    }

    return 'Unassigned partner';
  }

  private initializeForRoute(clientId: number | null, caseId: number | null): void {
    this.clientId.set(clientId);
    this.caseId.set(caseId);

    if (!clientId) {
      this.client.set(null);
      this.caseItem.set(null);
      this.notes.set([]);
      this.statusEvents.set([]);
      this.toastService.danger('Invalid URL', 'Invalid client reference in URL.');
      return;
    }

    this.loadClient(clientId);
    this.loadLookups();

    if (caseId) {
      this.loadCase(caseId, clientId);
      this.loadNotes(caseId, clientId);
      this.loadStatusEvents(caseId, clientId);
      return;
    }

    this.caseItem.set(null);
    this.notes.set([]);
    this.statusEvents.set([]);
  }

  private loadNotes(caseId: number, clientId: number): void {
    this.loadingNotes.set(true);

    this.casesService
      .listCaseNotes(caseId)
      .pipe(finalize(() => this.loadingNotes.set(false)))
      .subscribe({
        next: (response) => {
          if (this.clientId() !== clientId || this.caseId() !== caseId) {
            return;
          }

          this.notes.set(response.data ?? []);
        },
        error: (error) => {
          this.notes.set([]);
          this.toastService.danger(
            'Load Notes Failed',
            error?.error?.message ?? 'Unable to load case notes right now.',
          );
        },
      });
  }

  private loadStatusEvents(caseId: number, clientId: number): void {
    this.casesService
      .listCaseStatusEvents(caseId)
      .subscribe({
        next: (response) => {
          if (this.clientId() !== clientId || this.caseId() !== caseId) {
            return;
          }

          this.statusEvents.set(response.data ?? []);
        },
        error: () => {
          this.statusEvents.set([]);
          this.toastService.danger(
            'Load Timeline Failed',
            'Unable to load case status history right now.',
          );
        },
      });
  }

  private loadClient(clientId: number): void {
    this.loadingClient.set(true);

    this.clientsService
      .getClientDetail(clientId)
      .pipe(finalize(() => this.loadingClient.set(false)))
      .subscribe({
        next: (response) => {
          this.client.set(response.client);
        },
        error: (error) => {
          this.client.set(null);
          this.toastService.danger(
            'Load Client Failed',
            error?.error?.message ?? 'Unable to load client details.',
          );
        },
      });
  }

  private loadLookups(): void {
    this.loadingLookups.set(true);

    forkJoin({
      services: this.casesService.listServices(),
      partners: this.casesService.listPartners(),
    })
      .pipe(finalize(() => this.loadingLookups.set(false)))
      .subscribe({
        next: ({ services, partners }) => {
          this.services.set((services.data ?? []).map((item) => ({
            id: item.id,
            name: item.name,
            unitLabel: item.service_unit_label?.trim() || 'Units',
          })));
          const allPartners = (partners.data ?? []).map((item) => ({ id: item.id, name: item.name }));
          this.partners.set(allPartners);

          if (this.mode() === 'create') {
            const serviceId = this.caseForm.controls.serviceId.getRawValue();

            if (serviceId <= 0 && this.services().length > 0) {
              this.caseForm.controls.serviceId.setValue(this.services()[0].id);
              return;
            }

            if (serviceId > 0) {
              this.loadPartnersByService(serviceId);
            }
          }
        },
        error: () => {
          this.services.set([]);
          this.partners.set([]);
          this.toastService.danger('Lookup Failed', 'Unable to load services and partners right now.');
        },
      });
  }

  private loadPartnersByService(serviceId: number): void {
    this.casesService.listApprovedPartnersForService(serviceId).subscribe({
      next: (response) => {
        const approvedPartners = (response.data ?? []).map((item) => ({ id: item.id, name: item.name }));
        this.partners.set(approvedPartners);

        const currentPartnerId = this.caseForm.controls.partnerOrganizationId.getRawValue();
        const stillValid = approvedPartners.some((partner) => partner.id === currentPartnerId);
        if (!stillValid) {
          this.caseForm.controls.partnerOrganizationId.setValue(approvedPartners[0]?.id ?? 0);
        }
      },
      error: (error) => {
        this.partners.set([]);
        this.caseForm.controls.partnerOrganizationId.setValue(0);
        this.toastService.danger(
          'Load Partners Failed',
          error?.error?.message ?? 'Unable to load approved partners for the selected service.',
        );
      },
    });
  }

  private loadCase(caseId: number, clientId: number): void {
    this.loadingCase.set(true);

    this.casesService
      .getCase(caseId)
      .pipe(finalize(() => this.loadingCase.set(false)))
      .subscribe({
        next: ({ data }) => {
          if (data.clientId !== clientId) {
            this.caseItem.set(null);
            this.toastService.danger(
              'Case Mismatch',
              'This case does not belong to the selected client.',
            );
            return;
          }

          this.caseItem.set(data);
        },
        error: (error) => {
          this.caseItem.set(null);
          this.toastService.danger(
            'Load Case Failed',
            error?.error?.message ?? 'Unable to load case details right now.',
          );
        },
      });
  }

  private refreshCaseSummary(caseId: number): void {
    this.casesService.getCase(caseId).subscribe({
      next: ({ data }) => {
        this.caseItem.set(data);
      },
    });
  }

  private computeDurationMinutes(startTime: string, endTime: string): number {
    const [startHour, startMinute] = startTime.split(':').map((value) => Number(value));
    const [endHour, endMinute] = endTime.split(':').map((value) => Number(value));
    const startTotal = (startHour * 60) + startMinute;
    const endTotal = (endHour * 60) + endMinute;
    return endTotal - startTotal;
  }

  private todayDateString(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private parsePositiveInt(value: string | null): number | null {
    if (!value) {
      return null;
    }

    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed <= 0) {
      return null;
    }

    return parsed;
  }
}
